import { useQuery } from '@apollo/client/react';
import { useRouter } from 'expo-router';
import { useState } from 'react';
import { FAB, Portal } from 'react-native-paper';

import { ConfirmDialog } from '@/components/atoms/dialog';
import { StartRecordingNote } from '@/components/molecules/voice-recording';
import { MeDocument } from '@/generated/usermobile-bff/graphql';
import { useAiTranscriptionConsentDialog } from '@/hooks';
import { useTranslation } from '@/i18n';
import { useVoiceRecordingStore } from '@/stores';

interface ContactRecordingFabProps {
  contactId: number;
}

export const ContactRecordingFab = ({ contactId }: ContactRecordingFabProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [showStartDialog, setShowStartDialog] = useState(false);
  const { handleAccept, consentDialog, isConsentVisible } = useAiTranscriptionConsentDialog();
  const { data: userData } = useQuery(MeDocument);

  const recording = useVoiceRecordingStore((state) => state.recording);
  const requestMicrophonePermission = useVoiceRecordingStore(
    (state) => state.requestMicrophonePermission,
  );

  const { status } = recording;
  const isRecordingUnavailable = status !== 'idle';

  const handleRequestStartRecording = () => {
    if (!userData?.me || isRecordingUnavailable) {
      return;
    }
    const { accountId, backendAppUserId } = userData.me;

    if (accountId == null || backendAppUserId == null) {
      return;
    }

    setOpen(false);
    handleAccept(() => {
      setShowStartDialog(true);
    });
  };

  const handleConfirmStartRecording = async () => {
    if (!userData?.me || isRecordingUnavailable) {
      return;
    }
    const { accountId, backendAppUserId } = userData.me;

    if (accountId == null || backendAppUserId == null) {
      return;
    }

    setShowStartDialog(false);

    const granted = await requestMicrophonePermission();
    if (!granted) {
      return;
    }

    router.push({
      pathname: '/recording',
      params: {
        contactId: String(contactId),
        userId: String(backendAppUserId),
        accountId: String(accountId),
      },
    });
  };

  const handleDismissStartRecording = () => {
    setShowStartDialog(false);
  };

  if (isRecordingUnavailable) {
    return null;
  }

  return (
    <>
      <Portal>
        <FAB.Group
          open={open}
          visible
          icon={open ? 'close' : 'plus'}
          actions={[
            {
              icon: 'microphone',
              label: t('recording.actions.recordVoice'),
              onPress: handleRequestStartRecording,
            },
          ]}
          onStateChange={({ open }) => setOpen(open)}
          onPress={() => {}}
          style={{ paddingBottom: 16 }}
          testID="fab-group"
        />
      </Portal>
      {consentDialog}
      <ConfirmDialog
        visible={showStartDialog && !isConsentVisible}
        title={t('recording.startConfirmation.title')}
        body={t('recording.startConfirmation.body')}
        note={<StartRecordingNote />}
        confirmLabel={t('recording.startConfirmation.confirm')}
        dismissLabel={t('recording.startConfirmation.dismiss')}
        onConfirm={handleConfirmStartRecording}
        onDismiss={handleDismissStartRecording}
      />
    </>
  );
};
